import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import '../components/Blog.css';
import { auth, db } from '../components/firebase';


const ADMIN_EMAIL = 'ronak.r.shah@gmail.com';


const starterBlogs = [
  {
    id: 1,
    category: 'Financial Blogs',
    title: 'How to Build a Strong Emergency Fund',
    subtitle: 'A simple plan to create 3 to 6 months of protection',
    author: 'Will to Wealth Team',
    date: 'June 2026',
    content: `
A strong emergency fund is one of the first building blocks of financial confidence.

Start by reviewing your monthly essential expenses such as housing, groceries, transportation, insurance, and utilities. From there, set a target amount that covers at least 3 to 6 months of those core costs.

Automating a small transfer each week or month can make the process easier. The goal is not perfection on day one. The goal is consistency.

An emergency reserve can help families avoid high-interest debt when life becomes unpredictable.
    `.trim(),
  },
  {
    id: 2,
    category: 'Financial Blogs',
    title: 'Debt Reduction Strategies That Actually Work',
    subtitle: 'Practical ways to reduce financial pressure step by step',
    author: 'Will to Wealth Team',
    date: 'June 2026',
    content: `
Debt reduction starts with clarity.

List every debt, the balance, the interest rate, and the minimum payment. Once you can see the full picture, you can choose a strategy such as focusing on the highest-interest balance first or paying off the smallest balance first for faster momentum.

At the same time, review spending habits and look for areas where cash flow can improve. Even small monthly changes can free up money for faster repayment.

The most effective debt plan is one you can realistically follow for the long term.
    `.trim(),
  },
  {
    id: 3,
    category: 'Financial Blogs',
    title: 'How Life Insurance Fits Into a Family Financial Plan',
    subtitle: 'Protection planning that supports long-term stability',
    author: 'Will to Wealth Team',
    date: 'June 2026',
    content: `
Life insurance is not only about replacing income after a loss. It can also be part of a broader family protection strategy.

A thoughtful review should look at dependents, debt, future education needs, final expenses, and long-term financial obligations. The amount and type of coverage should match the family’s stage of life.

The goal is to protect people from financial disruption at the moments when support matters most.

Good protection planning creates confidence, not confusion.
    `.trim(),
  },
  {
    id: 4,
    category: 'Real Estate Blogs',
    title: 'When Are You Ready to Buy a House?',
    subtitle: 'Financial signs that tell you it may be the right time',
    author: 'Will to Wealth Team',
    date: 'June 2026',
    content: `
Buying a home is not only about wanting more space. It is also about financial readiness.

Before buying, review your credit profile, emergency savings, monthly debt obligations, and expected ownership costs. A down payment is important, but so is being ready for maintenance, taxes, insurance, and unexpected repairs.

A healthy home purchase is one that supports your long-term goals instead of putting them at risk.

The right time to buy is when the numbers support the decision with confidence.
    `.trim(),
  },
  {
    id: 5,
    category: 'Real Estate Blogs',
    title: 'First-Time Homebuyer Mistakes to Avoid',
    subtitle: 'What to watch before making a major property decision',
    author: 'Will to Wealth Team',
    date: 'June 2026',
    content: `
Many first-time buyers focus only on the monthly mortgage payment.

In reality, a property decision should include closing costs, moving costs, insurance, taxes, maintenance, and a reserve for repairs. Skipping this bigger view can create stress very quickly.

It is also important to avoid shopping above your comfort zone. A home should support your lifestyle, not strain it.

A smart real-estate decision balances emotion with preparation.
    `.trim(),
  },
  {
    id: 6,
    category: 'Real Estate Blogs',
    title: 'How Real Estate Fits Into Long-Term Wealth Planning',
    subtitle: 'A bigger-picture look at property and financial growth',
    author: 'Will to Wealth Team',
    date: 'June 2026',
    content: `
Real estate can play a valuable role in a long-term financial strategy when it is approached carefully.

A primary home can provide stability, while investment property may create income and long-term appreciation. But every property decision should be reviewed alongside cash flow, debt levels, and overall risk.

Real estate works best when it fits within a broader plan rather than becoming the whole plan.

The strongest strategy is one that keeps flexibility, protection, and long-term growth in balance.
    `.trim(),
  },
  {
    id: 7,
    category: 'Business Blogs',
    title: 'Why Financial Education Matters for Business Owners',
    subtitle: 'Better decisions start with better understanding',
    author: 'Will to Wealth Team',
    date: 'June 2026',
    content: `
Business owners make financial decisions every week.

That is why financial education is so important. Understanding cash flow, protection needs, debt management, tax awareness, and long-term planning can improve both personal and business stability.

A business can generate income, but income alone does not guarantee lasting wealth. Structure, discipline, and planning matter.

The better your understanding, the stronger your business decisions become.
    `.trim(),
  },
  {
    id: 8,
    category: 'Business Blogs',
    title: 'How to Improve Cash Flow in a Growing Business',
    subtitle: 'Small changes that can create more breathing room',
    author: 'Will to Wealth Team',
    date: 'June 2026',
    content: `
Cash flow is one of the most important measures of business health.

Start by reviewing recurring costs, payment timelines, pricing, and collection delays. Many businesses are profitable on paper but still experience pressure because cash arrives too slowly or leaves too quickly.

Improving invoicing systems, reducing waste, and forecasting monthly needs can make a major difference.

Healthy cash flow creates room for growth, hiring, and smarter long-term decisions.
    `.trim(),
  },
  {
    id: 9,
    category: 'Business Blogs',
    title: 'Building a Business With Long-Term Vision',
    subtitle: 'Growth becomes stronger when strategy leads the way',
    author: 'Will to Wealth Team',
    date: 'June 2026',
    content: `
A strong business is built on more than short-term sales.

Long-term growth requires planning, clear goals, disciplined spending, leadership development, and a commitment to serving people well. Vision helps business owners make better decisions in difficult seasons.

Businesses that plan ahead are often more resilient when markets change.

Sustainable growth comes from alignment between purpose, operations, and financial discipline.
    `.trim(),
  },
];

const categories = ['Financial Blogs', 'Real Estate Blogs', 'Business Blogs'];

function Blog() {
  const [posts, setPosts] = useState(starterBlogs);
  const [selectedCategory, setSelectedCategory] = useState('Financial Blogs');
  const [showAdmin, setShowAdmin] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [formData, setFormData] = useState({
    category: 'Financial Blogs',
    title: '',
    subtitle: '',
    author: '',
    date: '',
    content: '',
  });

  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
    });

    const q = query(collection(db, 'blogs'), orderBy('createdAt', 'desc'));

    const unsubscribeBlogs = onSnapshot(q, (snapshot) => {
      const firebasePosts = snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
      }));

      if (firebasePosts.length > 0) {
        setPosts(firebasePosts);
      } else {
        setPosts(starterBlogs);
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeBlogs();
    };
  }, []);

  const groupedPosts = useMemo(() => {
    return categories.map((category) => ({
      category,
      items: posts.filter((post) => post.category === category),
    }));
  }, [posts]);

  const visiblePosts = posts.filter(
    (post) => post.category === selectedCategory
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setEmail('');
      setPassword('');
      setShowAdmin(true);
    } catch (error) {
      alert('Login failed. Please check admin credentials.');
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setShowAdmin(false);
  };

  const handleAddPost = async (e) => {
    e.preventDefault();

    if (!isAdmin) {
      alert('Only admin can publish blogs.');
      return;
    }

    if (
      !formData.category ||
      !formData.title.trim() ||
      !formData.subtitle.trim() ||
      !formData.author.trim() ||
      !formData.date.trim() ||
      !formData.content.trim()
    ) {
      alert('Please fill in all fields before publishing.');
      return;
    }

    try {
      await addDoc(collection(db, 'blogs'), {
        category: formData.category,
        title: formData.title.trim(),
        subtitle: formData.subtitle.trim(),
        author: formData.author.trim(),
        date: formData.date.trim(),
        content: formData.content.trim(),
        createdAt: serverTimestamp(),
      });

      setSelectedCategory(formData.category);

      setFormData({
        category: 'Financial Blogs',
        title: '',
        subtitle: '',
        author: '',
        date: '',
        content: '',
      });

      setShowAdmin(false);
    } catch (error) {
      alert('Could not publish blog.');
    }
  };

  const handleDeletePost = async (id) => {
    if (!isAdmin) {
      alert('Only admin can delete blogs.');
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to delete this blog?'
    );

    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'blogs', id));
    } catch (error) {
      alert('Could not delete blog.');
    }
  };

  return (
    <main className="blog-page">
      <section className="blog-hero">
        <div className="blog-container">
          <div className="blog-hero-top">
            <div>
              <span className="blog-eyebrow">Blog</span>
              <h1 className="blog-title">
                Insights for <span>Money, Property, and Business</span>
              </h1>
              <p className="blog-intro">
                Explore practical articles on financial planning, real estate,
                business growth, protection, and long-term wealth building.
              </p>
            </div>

            <div className="blog-hero-actions">
              {isAdmin ? (
                <>
                  <button
                    type="button"
                    className="blog-btn blog-btn-primary"
                    onClick={() => setShowAdmin((prev) => !prev)}
                  >
                    {showAdmin ? 'Close Blog Uploader' : 'Open Blog Uploader'}
                  </button>

                  <button
                    type="button"
                    className="blog-btn blog-btn-secondary"
                    onClick={handleLogout}
                  >
                    Logout Admin
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="blog-btn blog-btn-primary"
                  onClick={() => setShowAdmin((prev) => !prev)}
                >
                  {showAdmin ? 'Close Admin Login' : 'Admin Login'}
                </button>
              )}
            </div>
          </div>

          {!authLoading && showAdmin && !isAdmin && (
            <div className="blog-admin-panel">
              <div className="blog-admin-header">
                <h2>Admin Login</h2>
                <p>Only admin can publish or delete blog posts.</p>
              </div>

              <form className="blog-admin-form" onSubmit={handleAdminLogin}>
                <div className="blog-form-grid">
                  <label className="blog-field">
                    <span>Email</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter admin email"
                    />
                  </label>

                  <label className="blog-field">
                    <span>Password</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                    />
                  </label>
                </div>

                <div className="blog-form-actions">
                  <button type="submit" className="blog-btn blog-btn-primary">
                    Login as Admin
                  </button>
                </div>
              </form>
            </div>
          )}

          {isAdmin && showAdmin && (
            <div className="blog-admin-panel">
              <div className="blog-admin-header">
                <h2>Owner Blog Upload</h2>
                <p>Add a new blog by filling in the fields below.</p>
              </div>

              <form className="blog-admin-form" onSubmit={handleAddPost}>
                <div className="blog-form-grid">
                  <label className="blog-field">
                    <span>Category</span>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                    >
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="blog-field">
                    <span>Author</span>
                    <input
                      type="text"
                      name="author"
                      value={formData.author}
                      onChange={handleChange}
                      placeholder="Enter author name"
                    />
                  </label>

                  <label className="blog-field blog-field-full">
                    <span>Blog Title</span>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter blog title"
                    />
                  </label>

                  <label className="blog-field blog-field-full">
                    <span>Subtitle</span>
                    <input
                      type="text"
                      name="subtitle"
                      value={formData.subtitle}
                      onChange={handleChange}
                      placeholder="Enter subtitle"
                    />
                  </label>

                  <label className="blog-field">
                    <span>Date</span>
                    <input
                      type="text"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      placeholder="June 2026"
                    />
                  </label>

                  <label className="blog-field blog-field-full">
                    <span>Blog Content</span>
                    <textarea
                      name="content"
                      value={formData.content}
                      onChange={handleChange}
                      rows="10"
                      placeholder="Write the full blog content here..."
                    />
                  </label>
                </div>

                <div className="blog-form-actions">
                  <button type="submit" className="blog-btn blog-btn-primary">
                    Publish Blog
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>

      <section className="blog-layout-section">
        <div className="blog-container blog-layout">
          <aside className="blog-sidebar">
            <div className="blog-sidebar-inner">
              {groupedPosts.map((group) => (
                <div key={group.category} className="blog-sidebar-group">
                  <button
                    type="button"
                    className={`blog-sidebar-category ${
                      selectedCategory === group.category ? 'active' : ''
                    }`}
                    onClick={() => setSelectedCategory(group.category)}
                  >
                    <span>{group.category}</span>
                    <small>{group.items.length} blogs</small>
                  </button>

                  <ul className="blog-sidebar-list">
                    {group.items.map((post) => (
                      <li key={post.id} className="blog-sidebar-subitem">
                        <span className="blog-link-main">{post.title}</span>
                        <span className="blog-link-sub">{post.subtitle}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </aside>

          <section className="blog-content">
            <div className="blog-category-header">
              <span className="blog-category-eyebrow">Selected Category</span>
              <h2>{selectedCategory}</h2>
              <p>Showing all blogs in this section one below the other.</p>
            </div>

            <div className="blog-post-stack">
              {visiblePosts.length > 0 ? (
                visiblePosts.map((post) => (
                  <article key={post.id} className="blog-post-card">
                    <div className="blog-post-topbar">
                      <div className="blog-post-meta">
                        <span className="blog-post-category">{post.category}</span>
                        <span className="blog-post-divider">•</span>
                        <span>{post.date}</span>
                        <span className="blog-post-divider">•</span>
                        <span>{post.author}</span>
                      </div>

                      {isAdmin && (
                        <button
                          type="button"
                          className="blog-delete-btn"
                          onClick={() => handleDeletePost(post.id)}
                        >
                          Delete Blog
                        </button>
                      )}
                    </div>

                    <h3 className="blog-post-title">{post.title}</h3>
                    <p className="blog-post-subtitle">{post.subtitle}</p>

                    <div className="blog-post-body">
                      {post.content.split('\n\n').map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                    </div>
                  </article>
                ))
              ) : (
                <div className="blog-post-card">
                  <h3 className="blog-post-title">No blogs in this category</h3>
                  <p className="blog-post-subtitle">
                    No blog posts are available here right now.
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}

export default Blog;